import { formatCurrency } from '@/lib/utils';
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Trash2, Receipt, Paperclip, Download, X, Pencil, Filter, ArrowUpCircle, ArrowDownCircle, CheckCircle, Clock, Link2 } from 'lucide-react';
import { format, startOfMonth } from 'date-fns';
import { Lock } from 'lucide-react';
import { useLockedPeriods } from '../hooks/useLockedPeriods';

const ExpensesPage = () => {
  const { api, user } = useAuth();
  const { lockedPeriodFor } = useLockedPeriods();
  const isAdmin = user?.role === 'admin';
  const [expenses, setExpenses] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removeExistingAttachment, setRemoveExistingAttachment] = useState(false);
  const fileInputRef = useRef(null);
  
  // Filter states
  const [dateFrom, setDateFrom] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [showFilters, setShowFilters] = useState(false);

  const [formData, setFormData] = useState({
    business_id: '',
    amount: '',
    expense_date: format(new Date(), 'yyyy-MM-dd'),
    supplier_reason: '',
    movement_type: 'cash_out',
    comments: ''
  });
  const [attachment, setAttachment] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expensesRes, businessesRes] = await Promise.all([
        api.get('/cash-expenses'),
        api.get('/api-keys/all')
      ]);
      setExpenses(expensesRes.data);
      setBusinesses(businessesRes.data);
      if (businessesRes.data.length > 0) {
        setFormData(prev => ({ ...prev, business_id: businessesRes.data[0].id }));
      }
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('File type not allowed. Use JPEG, PNG, GIF or PDF.');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 5MB.');
        return;
      }
      setAttachment(file);
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setFormData({
      business_id: businesses[0]?.id || '',
      amount: '',
      expense_date: format(new Date(), 'yyyy-MM-dd'),
      supplier_reason: '',
      movement_type: 'cash_out',
      comments: ''
    });
    setAttachment(null);
    setEditingExpense(null);
    setRemoveExistingAttachment(false);
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      business_id: expense.business_id,
      amount: expense.amount.toString(),
      expense_date: expense.expense_date,
      supplier_reason: expense.supplier_reason,
      movement_type: expense.movement_type || 'cash_out',
      comments: expense.comments || ''
    });
    setAttachment(null);
    setRemoveExistingAttachment(false);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.business_id || !formData.amount || !formData.supplier_reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      // Use FormData for file upload
      const submitData = new FormData();
      submitData.append('business_id', formData.business_id);
      submitData.append('amount', formData.amount);
      submitData.append('expense_date', formData.expense_date);
      submitData.append('supplier_reason', formData.supplier_reason);
      submitData.append('movement_type', formData.movement_type);
      if (formData.comments) {
        submitData.append('comments', formData.comments);
      }
      if (attachment) {
        submitData.append('attachment', attachment);
      }
      if (removeExistingAttachment) {
        submitData.append('remove_attachment', 'true');
      }

      if (editingExpense) {
        // Update existing expense
        await api.put(`/cash-expenses/${editingExpense.id}`, submitData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Movement updated');
      } else {
        // Create new expense
        await api.post('/cash-expenses/upload', submitData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success(formData.movement_type === 'cash_in' ? 'Cash In recorded' : 'Cash Out recorded');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadAttachment = async (expenseId, filename) => {
    try {
      const response = await api.get(`/cash-expenses/${expenseId}/attachment`, {
        responseType: 'blob'
      });
      
      // Try to get filename from Content-Disposition header
      let downloadFilename = filename;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          downloadFilename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // If still no filename, try to detect from content type
      if (!downloadFilename || downloadFilename === 'attachment') {
        const contentType = response.headers['content-type'];
        if (contentType) {
          if (contentType.includes('pdf')) downloadFilename = 'attachment.pdf';
          else if (contentType.includes('jpeg') || contentType.includes('jpg')) downloadFilename = 'attachment.jpg';
          else if (contentType.includes('png')) downloadFilename = 'attachment.png';
          else if (contentType.includes('gif')) downloadFilename = 'attachment.gif';
          else downloadFilename = 'attachment';
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadFilename || 'attachment');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download attachment');
    }
  };

  const handleDelete = async (expenseId) => {
    try {
      await api.delete(`/cash-expenses/${expenseId}`);
      toast.success('Expense deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
    }
  };

  const handleConfirm = async (expenseId) => {
    try {
      await api.post(`/cash-expenses/${expenseId}/confirm`);
      toast.success('Expense confirmed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm expense');
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'matched':
        return <Badge className="bg-green-100 text-green-800 border-green-300"><Link2 className="h-3 w-3 mr-1" />Matched</Badge>;
      case 'confirmed':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-300"><CheckCircle className="h-3 w-3 mr-1" />Confirmed</Badge>;
      case 'pending_match':
      default:
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    }
  };

  const resetFilters = () => {
    setDateFrom(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    setDateTo(format(new Date(), 'yyyy-MM-dd'));
    setSelectedBusiness('all');
    setSelectedStatus('all');
  };

  // Apply filters
  const filteredExpenses = expenses.filter(e => {
    // Business filter
    if (selectedBusiness !== 'all' && e.business_id !== selectedBusiness) return false;
    
    // Status filter
    if (selectedStatus !== 'all' && (e.status || 'pending_match') !== selectedStatus) return false;
    
    // Date filter
    if (dateFrom && e.expense_date < dateFrom) return false;
    if (dateTo && e.expense_date > dateTo) return false;
    
    return true;
  });

  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
  const pendingCount = expenses.filter(e => (e.status || 'pending_match') === 'pending_match').length;

  if (loading) {
    return (
      <div className="page-container">
        <Skeleton className="h-10 w-64 mb-6" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="page-container animate-slide-in">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A18]">Cash Movement</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Record cash in and cash out movements
            {pendingCount > 0 && (
              <Badge className="ml-2 bg-yellow-100 text-yellow-800">{pendingCount} pending</Badge>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? 'bg-[#006241]/10' : ''}
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#006241] hover:bg-[#004d33]" data-testid="add-expense-btn">
                <Plus className="h-4 w-4 mr-2" />
                Record Movement
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingExpense ? 'Edit Cash Movement' : 'Record Cash Movement'}</DialogTitle>
              <DialogDescription>
                {editingExpense ? 'Update the movement details' : 'Record a cash in or cash out movement'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Movement Type Toggle */}
              <div className="space-y-2">
                <Label>Movement Type</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={formData.movement_type === 'cash_in' ? 'default' : 'outline'}
                    className={formData.movement_type === 'cash_in' ? 'bg-green-600 hover:bg-green-700 flex-1' : 'flex-1'}
                    onClick={() => setFormData(prev => ({ ...prev, movement_type: 'cash_in' }))}
                    data-testid="cash-in-btn"
                  >
                    <ArrowUpCircle className="h-4 w-4 mr-2" />
                    Cash In
                  </Button>
                  <Button
                    type="button"
                    variant={formData.movement_type === 'cash_out' ? 'default' : 'outline'}
                    className={formData.movement_type === 'cash_out' ? 'bg-red-600 hover:bg-red-700 flex-1' : 'flex-1'}
                    onClick={() => setFormData(prev => ({ ...prev, movement_type: 'cash_out' }))}
                    data-testid="cash-out-btn"
                  >
                    <ArrowDownCircle className="h-4 w-4 mr-2" />
                    Cash Out
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Business</Label>
                <Select 
                  value={formData.business_id} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, business_id: value }))}
                >
                  <SelectTrigger data-testid="expense-business-select">
                    <SelectValue placeholder="Select business" />
                  </SelectTrigger>
                  <SelectContent>
                    {businesses.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.business_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  className="font-mono"
                  required
                  data-testid="expense-amount-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, expense_date: e.target.value }))}
                  required
                  data-testid="expense-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Supplier / Reason</Label>
                <Input
                  type="text"
                  value={formData.supplier_reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, supplier_reason: e.target.value }))}
                  placeholder="e.g. Milk supplier, Petty cash"
                  required
                  data-testid="expense-reason-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Comments (Optional)</Label>
                <Textarea
                  value={formData.comments}
                  onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                  placeholder="Additional notes..."
                  rows={2}
                  data-testid="expense-comments-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Attachment {editingExpense?.has_attachment && !removeExistingAttachment ? '(Replace or Remove)' : '(Optional)'}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.pdf"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="expense-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1"
                  >
                    <Paperclip className="h-4 w-4 mr-2" />
                    {attachment ? 'Change File' : 'Attach File'}
                  </Button>
                </div>
                {attachment && (
                  <div className="flex items-center gap-2 p-2 bg-muted rounded-md text-sm">
                    <Paperclip className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1 truncate">{attachment.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={removeAttachment}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                {editingExpense?.has_attachment && !attachment && !removeExistingAttachment && (
                  <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-md text-sm">
                    <Paperclip className="h-4 w-4 text-blue-600" />
                    <span className="flex-1 truncate text-blue-700">{editingExpense.attachment_filename}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setRemoveExistingAttachment(true)}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Remove
                    </Button>
                  </div>
                )}
                {removeExistingAttachment && !attachment && (
                  <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-md text-sm">
                    <span className="text-red-700">Attachment will be removed</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 text-blue-600 hover:text-blue-700"
                      onClick={() => setRemoveExistingAttachment(false)}
                    >
                      Undo
                    </Button>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Accepted: JPEG, PNG, GIF, PDF (max 5MB)
                </p>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={saving}
                  className={formData.movement_type === 'cash_in' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                  data-testid="expense-submit-btn"
                >
                  {saving ? 'Saving...' : (editingExpense ? 'Update Movement' : (formData.movement_type === 'cash_in' ? 'Record Cash In' : 'Record Cash Out'))}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <Card className="mb-4 border-[#006241]/20" data-testid="filter-panel">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Business</Label>
                <Select value={selectedBusiness} onValueChange={setSelectedBusiness}>
                  <SelectTrigger className="w-[200px]" data-testid="expense-business-filter">
                    <SelectValue placeholder="All Businesses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Businesses</SelectItem>
                    {businesses.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.business_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">From Date</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-[150px]"
                  data-testid="expense-date-from"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">To Date</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-[150px]"
                  data-testid="expense-date-to"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Status</Label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-[150px]" data-testid="expense-status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending_match">Pending</SelectItem>
                    <SelectItem value="matched">Matched</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <X className="h-4 w-4 mr-1" />
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Bar */}
      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
        <span className="text-sm text-muted-foreground">
          {filteredExpenses.length} movement{filteredExpenses.length !== 1 ? 's' : ''}
          {selectedBusiness !== 'all' || dateFrom || dateTo ? ' (filtered)' : ''}
        </span>
        <div className="ml-auto flex gap-3">
          <div className="px-4 py-2 bg-green-50 rounded-lg">
            <span className="text-sm text-muted-foreground">In: </span>
            <span className="font-mono font-medium text-green-700">
              {formatCurrency(filteredExpenses.filter(e => e.movement_type === 'cash_in').reduce((sum, e) => sum + e.amount, 0))}
            </span>
          </div>
          <div className="px-4 py-2 bg-red-50 rounded-lg">
            <span className="text-sm text-muted-foreground">Out: </span>
            <span className="font-mono font-medium text-red-700">
              {formatCurrency(filteredExpenses.filter(e => e.movement_type !== 'cash_in').reduce((sum, e) => sum + e.amount, 0))}
            </span>
          </div>
        </div>
      </div>

      {/* Movements Table */}
      <Card data-testid="expenses-table">
        <CardContent className="p-0">
          {filteredExpenses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Receipt className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-[#1A1A18]">No movements recorded</h3>
              <p className="text-muted-foreground mt-1">Click "Record Movement" to add one</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F2F2F0]">
                    <TableHead>Date</TableHead>
                    <TableHead>Business</TableHead>
                    <TableHead>Supplier / Reason</TableHead>
                    <TableHead>Comments</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Attachment</TableHead>
                    <TableHead>Recorded By</TableHead>
                    {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExpenses.map((expense) => (
                    <TableRow key={expense.id}>
                      <TableCell className="font-medium">{formatDate(expense.expense_date)}</TableCell>
                      <TableCell>{expense.business_name}</TableCell>
                      <TableCell>
                        <div>{expense.supplier_reason}</div>
                        {expense.branch_tag && (
                          <span className="text-xs text-muted-foreground">[{expense.branch_tag}]</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-xs truncate">
                        {expense.comments || '-'}
                      </TableCell>
                      <TableCell className={`text-right font-mono ${expense.movement_type === 'cash_in' ? 'text-green-700' : 'text-red-700'}`}>
                        {expense.movement_type === 'cash_in' ? '+' : '-'}{formatCurrency(expense.amount)}
                      </TableCell>
                      <TableCell>
                        {expense.movement_type === 'cash_in' ? (
                          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                            <ArrowUpCircle className="h-3 w-3 mr-1" />
                            In
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">
                            <ArrowDownCircle className="h-3 w-3 mr-1" />
                            Out
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(expense.status || 'pending_match')}
                      </TableCell>
                      <TableCell>
                        {expense.has_attachment ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadAttachment(expense.id, expense.attachment_filename)}
                            className="text-[#006241]"
                            data-testid={`download-attachment-${expense.id}`}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {expense.created_by}
                      </TableCell>
                      {isAdmin && lockedPeriodFor(expense.business_id, expense.expense_date) && (
                        <TableCell>
                          <div className="flex justify-end items-center gap-1 text-xs text-muted-foreground" title={`Reconciled period: ${lockedPeriodFor(expense.business_id, expense.expense_date).name}`} data-testid={`expense-locked-${expense.id}`}>
                            <Lock className="h-4 w-4" /> Locked
                          </div>
                        </TableCell>
                      )}
                      {isAdmin && !lockedPeriodFor(expense.business_id, expense.expense_date) && (
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            {(expense.status || 'pending_match') === 'pending_match' && (
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => handleConfirm(expense.id)}
                                title="Confirm (not a shift payout)"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleEdit(expense)}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700" title="Delete">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Expense?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will remove the expense and update the ledger. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(expense.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExpensesPage;
